import {
  PreloadedQuery,
  UseQueryLoaderLoadQueryOptions,
  graphql,
  useLazyLoadQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { Patient } from "../../../types/patient";
import { Component, ErrorInfo, ReactNode, useCallback, useEffect, useState } from "react";

import {
  AnonymousPatientAuthProvider,
  PatientAuthProvider,
} from "./PatientAuthProvider";
import { PatientAuthQueryQuery } from "./__generated__/PatientAuthQueryQuery.graphql";
import { Card, Skeleton } from "antd";
import { Outlet, useSearchParams } from "react-router-dom";

// ipalladino+15@gmail.com
// 1234

export const MY_DETAILS_QUERY_PAUTH = graphql`
  query PatientAuthQueryQuery {
    myPatientDetails {
      transactions {
        id
        paymentAmount
        paymentMethodType
        transactionType
        quote {
          id
        }
      }
      id
      firstName
      lastName
      zipCode
      dateOfBirth
      phone
      email
      emailConfirmed
      quotes {
        id
        createdAt
        updatedAt
        name
        lastName
        age
        email
        phone
        zipCode
        selectedLocation {
          id
          name
        }
        selectedSurgeon {
          id
          name
          image
        }
        selectedProceduresV2 {
          id
          name
        }
        heightFt
        heightIn
        weight
        fileList {
          response {
            url
          }
        }
        smoker
        hasMedicalConditions
        medicalConditions
        otherMedicalConditionDescription
        lost50pounds
        priorCosmeticSurgery
        priorCosmeticSurgeryDescription
        procedureDescription
        addOns
        activeInvoice
        contracts {
          id
          storedContractKey
          contract {
            id
            name
          }
        }
        invoice {
          itemType
          addon {
            id
            name
          }
          procedure {
            name
          }
        }
        activeFinalPrice
        maxQuoted
        minQuoted
        status {
          id
          name
          position
          color
        }
        surgeryBooking {
          id
          surgeryDate
          createdAt
          updatedAt
        }
        tasksCompleted {
          id
          createdAt
          task {
            id
            task
            actionRequired
            icon
            position
            createdAt
          }
        }
        labsRequested {
          id
          labsOrderPDF
          createdAt
        }
      }
    }
  }
`;

interface PatientAuthQueryProps {
  children?: ReactNode;
}

interface PatientAuthBoundaryProps {
  fallback: ReactNode;
  children: ReactNode;
}

interface PatientAuthBoundaryState {
  hasError: boolean;
}

class PatientAuthErrorBoundary extends Component<
  PatientAuthBoundaryProps,
  PatientAuthBoundaryState
> {
  state: PatientAuthBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PatientAuthBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn(
      "[PatientAuthQuery] query failed, falling back to anonymous context",
      error,
      info,
    );
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

export const PatientAuthQuery = ({ children }: PatientAuthQueryProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Consume an OAuth / email-confirmation handoff token (?token=<JWT>) BEFORE
  // the auth query fires, so the very first myPatientDetails request is
  // authenticated. The server redirects patients here as
  //   {CLIENT_URL}/patient-portal/my-account?token=<JWT>
  // after Google sign-in (server/routes/patient-auth-routes.ts) and after
  // email confirmation (the /confirm/:token route). useState's initializer
  // runs once, synchronously, DURING the first render — i.e. before any
  // effect, including the loadQuery effect below — so the token is already in
  // localStorage when fetchQuery (relayEnvironment.ts) reads it. This replaces
  // the old reload-based handoff in PatientConfirmedProtectedRoute, which
  // fired only AFTER an unauthenticated first render had returned
  // <Navigate to="/patient-portal/sign-in"> and then forced
  // window.location.reload() — a race that left patients stuck on an
  // indefinite loading state right after picking their Google account.
  useState(() => {
    const handoffToken = searchParams.get("token");
    if (handoffToken) {
      localStorage.setItem("token", handoffToken);
    }
  });

  const [queryRef, loadQuery] = useQueryLoader<PatientAuthQueryQuery>(
    MY_DETAILS_QUERY_PAUTH
  );

  // Belt-and-suspenders with the isQuotePage() exclude in index.html: the
  // patient portal shares the …/quote/ basename, so the pre-React curtain
  // would otherwise show the funnel procedures grid here. The index.html
  // exclude hides it immediately on load; this dismisses it on mount too in
  // case that path match ever misses (mirrors the admin AuthenticationProvider
  // and the /action/ pages). See docs/infrastructure/PATIENT_FACING_EMAIL_LINKS.md.
  //
  // BUT this component also wraps the public funnel (pages/quote.tsx). There,
  // QuoteApp owns the boot curtain and lifts it only once the real funnel
  // content has painted. Dismissing it here would fire on PatientAuthQuery's
  // FIRST commit — while we're still rendering the <Skeleton> below and before
  // QuoteApp has mounted — lifting the opaque curtain to reveal that paragraph
  // skeleton for a frame (a visible flash). So restrict the dismiss to the
  // patient-portal paths it was actually meant for; on the funnel, leave the
  // curtain to QuoteApp.
  useEffect(() => {
    // Keep this test in sync with isQuotePage()'s /patient-portal exclude in
    // client/public/index.html — both decide curtain visibility for portal
    // paths; a portal-route rename must update both.
    const onPatientPortal = /\/patient-portal(\/|$)/.test(
      window.location.pathname,
    );
    if (!onPatientPortal) return;
    (window as any).hideInitialLoading?.();
  }, []);

  // Strip the consumed token from the URL so it isn't left in browser history
  // or copy-pasted into a shareable link. Safe to run after the query has been
  // kicked off with it — the token now lives in localStorage. replace:true
  // keeps the tokenless URL from adding a back-button trap.
  useEffect(() => {
    if (searchParams.has("token")) {
      searchParams.delete("token");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  const refresh = useCallback(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  // Ensure to handle the potential null queryRef in the provider
  if (!queryRef)
    return (
      <Card>
        <Skeleton active />
      </Card>
    );

  const anonymousFallback = (
    <AnonymousPatientAuthProvider refresh={refresh}>
      {children}
      <Outlet />
    </AnonymousPatientAuthProvider>
  );

  return (
    <PatientAuthErrorBoundary fallback={anonymousFallback}>
      <PatientAuthProvider queryRef={queryRef} refresh={refresh}>
        {children}
        <Outlet />
      </PatientAuthProvider>
    </PatientAuthErrorBoundary>
  );
};
